import { useEffect, useRef, useState } from 'react'
import { IconoQuitar } from '../../componentes/iconos'
import type { ProductoCatalogo } from './catalogo'
import { bonosDelCatalogo, esBono } from './catalogo'
import { formatearEuros, formatearFecha } from './formato'
import { compararPrecios, hayDiferencia, totalAnterior, totalBase } from './precios'
import type { Historial, LineaPedido } from './precios'
import type { ProductoElegido } from './SelectorProductos'
import { SelectorProductos } from './SelectorProductos'

/**
 * Las lineas que se van a publicar, con el precio de catalogo **al lado de lo
 * que ese cliente pago la ultima vez**.
 *
 * Es la salvaguarda acordada en el diseno. El precio sale del catalogo de Odoo,
 * y el catalogo esta desfasado: sin editar nada, un BONO 4 pasa de 35 EUR a 32.
 * Que la diferencia se vea aqui, antes de publicar, es lo que separa una
 * decision de un descuido. Cualquier precio se puede corregir a mano.
 *
 * La comparacion es por linea y sobre la **base imponible**, que es lo que
 * guarda Odoo en `price_unit`. El total con IVA del pedido anterior no se
 * enfrenta a nada: compararlo con la base marcaria un 21% de diferencia que no
 * existe.
 */

interface Props {
  lineas: LineaPedido[]
  historial: Historial | undefined
  /** Para poder decir "todavia no ha llegado" en vez de "no hay". */
  cargandoHistorial?: boolean
  onCambiarPrecio: (indice: number, precio: number | null) => void
  /**
   * El catalogo de Odoo, para poder anadirle productos a **este** cliente. Si
   * no llega, el pedido se ensena sin poder tocarlo (es lo que hacia antes).
   */
  catalogo?: ProductoCatalogo[]
  /** Los productos anadidos a mano a este cliente, sin las lineas del backend. */
  anadidas?: LineaPedido[]
  onCambiarAnadidas?: (lineas: LineaPedido[]) => void
  /** Cambiar la cantidad de una linea (Opcion A). `null` = volver a la del backend. */
  onCambiarCantidad?: (indice: number, cantidad: number | null) => void
  /** Elegir otro bono en la linea (Opcion A). `null` = volver al del backend. */
  onCambiarBono?: (
    indice: number,
    bono: { nombre: string; product_id?: number | null; precio: number } | null,
  ) => void
  /** Indices de lineas quitadas a mano (Opcion A). Se tachan y no se publican. */
  eliminadas?: number[]
  /** Quitar o restaurar una linea por su indice (Opcion A). */
  onAlternarEliminada?: (indice: number) => void
}

/** Las lineas anadidas van al final, asi que su indice empieza aqui. */
function primeraAnadida(lineas: LineaPedido[], anadidas: LineaPedido[]): number {
  return lineas.length - anadidas.length
}

function aElegidos(anadidas: LineaPedido[]): ProductoElegido[] {
  return anadidas.map((linea, indice) => ({
    // Una linea sin `product_id` no puede pasar por el selector (no hay
    // producto de catalogo detras), pero tampoco llega aqui: todo lo que se
    // anade en esta pantalla sale del catalogo.
    productoId: linea.product_id ?? -indice - 1,
    nombre: linea.nombre,
    cantidad: linea.cantidad,
    precio: linea.precio,
  }))
}

const CELDA = 'px-3 py-2 text-sm text-tinta-2'
const CABECERA =
  'border-b border-borde px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada'

/**
 * El precio de una linea, editable.
 *
 * Lleva estado propio y no se ata directamente al valor de fuera porque
 * **borrar el campo es un paso legitimo** para reescribirlo: sin el, vaciar el
 * hueco devolvia el precio del catalogo al instante y lo que se tecleaba a
 * continuacion se anadia detras (32 mas 31,82 daba 3231,82). Mientras el campo
 * esta vacio no hay precio editado, que es lo que el campo esta diciendo.
 */
function CampoPrecio({
  valor,
  etiqueta,
  onCambiar,
}: {
  valor: number
  etiqueta: string
  onCambiar: (precio: number | null) => void
}) {
  const [texto, setTexto] = useState(String(valor))
  const ultimo = useRef(valor)

  useEffect(() => {
    if (valor === ultimo.current) return
    ultimo.current = valor
    setTexto(String(valor))
  }, [valor])

  return (
    <input
      type="number"
      step="0.01"
      aria-label={etiqueta}
      value={texto}
      onChange={(evento) => {
        const escrito = evento.target.value
        setTexto(escrito)
        const numero = Number(escrito)
        ultimo.current = escrito === '' || Number.isNaN(numero) ? valor : numero
        onCambiar(escrito === '' || Number.isNaN(numero) ? null : numero)
      }}
      className="w-24 rounded-lg border border-borde-fuerte bg-superficie px-2 py-1 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
    />
  )
}

/**
 * La cantidad de una linea, editable (Opcion A). Igual criterio que CampoPrecio:
 * estado propio para que borrar el campo sea un paso legitimo. Acepta enteros,
 * incluidos negativos (la devolucion de envases va en negativo).
 */
function CampoCantidad({
  valor,
  etiqueta,
  onCambiar,
}: {
  valor: number
  etiqueta: string
  onCambiar: (cantidad: number | null) => void
}) {
  const [texto, setTexto] = useState(String(valor))
  const ultimo = useRef(valor)

  useEffect(() => {
    if (valor === ultimo.current) return
    ultimo.current = valor
    setTexto(String(valor))
  }, [valor])

  return (
    <input
      type="number"
      step="1"
      aria-label={etiqueta}
      value={texto}
      onChange={(evento) => {
        const escrito = evento.target.value
        setTexto(escrito)
        const numero = Number(escrito)
        const vacio = escrito === '' || escrito === '-' || Number.isNaN(numero)
        ultimo.current = vacio ? valor : numero
        onCambiar(vacio ? null : numero)
      }}
      className="w-20 rounded-lg border border-borde-fuerte bg-superficie px-2 py-1 text-center text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
    />
  )
}

/**
 * Selector de bono en la propia linea (Opcion A, P3). Solo aparece en las lineas
 * que YA son un bono: lista los bonos del catalogo del mismo litraje para poder
 * cambiar "BONO 8" por "BONO 4" sin bajar al panel de abajo. No toca las lineas
 * de envase/devolucion (libertad total: el administrador las cuadra a mano).
 */
function SelectorBonoLinea({
  nombre,
  catalogo,
  onElegir,
}: {
  nombre: string
  catalogo: ProductoCatalogo[]
  onElegir: (bono: { nombre: string; product_id?: number | null; precio: number } | null) => void
}) {
  const bonos = bonosDelCatalogo(catalogo)
  // El litraje del bono actual, para ofrecer solo los de su misma garrafa.
  const actual = bonos.find((b) => b.nombre === nombre)
  const mismos = actual ? bonos.filter((b) => b.litraje === actual.litraje) : bonos
  // Si el bono actual no esta en el catalogo, lo mantenemos como opcion elegida.
  const hayActual = mismos.some((b) => b.nombre === nombre)

  return (
    <select
      aria-label={`Bono de la linea ${nombre}`}
      value={nombre}
      onChange={(evento) => {
        const elegido = bonos.find((b) => b.nombre === evento.target.value)
        if (!elegido || elegido.nombre === nombre) {
          onElegir(null)
          return
        }
        onElegir({ nombre: elegido.nombre, product_id: elegido.id, precio: elegido.precio })
      }}
      className="max-w-[16rem] rounded-lg border border-borde-fuerte bg-superficie px-2 py-1 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
    >
      {!hayActual ? <option value={nombre}>{nombre}</option> : null}
      {mismos.map((bono) => (
        <option key={bono.id ?? bono.nombre} value={bono.nombre}>
          {bono.nombre}
        </option>
      ))}
    </select>
  )
}

function Marca({ children }: { children: string }) {
  return (
    <span className="ml-2 inline-flex rounded-full bg-atencion-fondo px-2 py-0.5 text-xs font-semibold text-atencion-tinta ring-1 ring-atencion-anillo">
      {children}
    </span>
  )
}

export function DetallePedido({
  lineas,
  historial,
  cargandoHistorial,
  onCambiarPrecio,
  catalogo,
  anadidas = [],
  onCambiarAnadidas,
  onCambiarCantidad,
  onCambiarBono,
  eliminadas = [],
  onAlternarEliminada,
}: Props) {
  const comparadas = compararPrecios(lineas, historial)
  const quitadas = new Set(eliminadas)
  // El total solo cuenta lo que se va a publicar: las lineas quitadas no suman.
  const total = totalBase(lineas.filter((_, indice) => !quitadas.has(indice)))
  const anterior = totalAnterior(historial)
  const diferencias = hayDiferencia(comparadas)
  const desde = primeraAnadida(lineas, anadidas)
  const editable = catalogo !== undefined && onCambiarAnadidas !== undefined
  // Opcion A: las cantidades y los bonos se editan en la propia linea cuando el
  // padre nos da los callbacks y hay catalogo detras (para listar los bonos).
  const cantidadEditable = onCambiarCantidad !== undefined
  // Opcion A: cualquier linea se puede quitar/restaurar (libertad total).
  const borrable = onAlternarEliminada !== undefined
  const bonoEditable = onCambiarBono !== undefined && catalogo !== undefined

  return (
    <div className="flex flex-col gap-2">
      {editable ? (
        <SelectorProductos
          catalogo={catalogo}
          elegidos={aElegidos(anadidas)}
          mostrarLista={false}
          onCambiar={(elegidos) =>
            onCambiarAnadidas?.(
              elegidos.map((elegido) => ({
                nombre: elegido.nombre,
                cantidad: elegido.cantidad,
                precio: elegido.precio,
                product_id: elegido.productoId,
              })),
            )
          }
        />
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
        <table aria-label="Lineas del pedido" className="min-w-full border-collapse">
          <thead className="bg-elevado">
            <tr>
              <th scope="col" className={CABECERA}>
                Producto
              </th>
              <th scope="col" className={CABECERA}>
                Cantidad
              </th>
              <th scope="col" className={CABECERA}>
                Precio de catalogo
              </th>
              <th scope="col" className={CABECERA}>
                Lo que pago la ultima vez
              </th>
              <th scope="col" className={CABECERA}>
                Subtotal
              </th>
              {editable ? (
                <th scope="col" className={CABECERA}>
                  <span className="sr-only">Quitar</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {comparadas.map((linea, indice) => {
              const quitada = quitadas.has(indice)
              const tdApagada = quitada ? 'text-tinta-apagada line-through' : ''
              return (
              <tr
                key={`${linea.nombre}-${indice}`}
                className={`border-b border-borde-suave last:border-b-0 ${quitada ? 'opacity-60' : ''}`}
              >
                <td className={CELDA}>
                  {quitada ? (
                    <span className="text-tinta-apagada line-through">{linea.nombre}</span>
                  ) : bonoEditable && esBono(linea.nombre) && catalogo ? (
                    <SelectorBonoLinea
                      nombre={linea.nombre}
                      catalogo={catalogo}
                      onElegir={(bono) => onCambiarBono?.(indice, bono)}
                    />
                  ) : (
                    linea.nombre
                  )}
                </td>
                {/* La devolucion de envases va en negativo: es lo que hace que
                    el pedido cuadre con el flujo actual. En Opcion A se puede
                    editar a mano (incluida la devolucion). */}
                <td className={CELDA}>
                  {quitada ? (
                    <span className={tdApagada}>{linea.cantidad}</span>
                  ) : cantidadEditable ? (
                    <CampoCantidad
                      valor={linea.cantidad}
                      etiqueta={`Cantidad de ${linea.nombre}`}
                      onCambiar={(cantidad) => onCambiarCantidad?.(indice, cantidad)}
                    />
                  ) : (
                    linea.cantidad
                  )}
                </td>
                <td className={CELDA}>
                  {quitada ? (
                    <span className={tdApagada}>{formatearEuros(linea.precio)}</span>
                  ) : (
                    <CampoPrecio
                      valor={linea.precio}
                      etiqueta={`Precio de ${linea.nombre}`}
                      onCambiar={(precio) => onCambiarPrecio(indice, precio)}
                    />
                  )}
                </td>
                <td className={CELDA}>
                  {linea.precioAnterior === null ? (
                    <span className="text-tinta-apagada">
                      {cargandoHistorial ? 'Buscando' : 'Sin dato'}
                    </span>
                  ) : (
                    <>
                      {formatearEuros(linea.precioAnterior)}
                      {linea.difiere ? <Marca>Distinto</Marca> : null}
                    </>
                  )}
                </td>
                <td className={CELDA}>
                  <span className={tdApagada}>{formatearEuros(linea.subtotal)}</span>
                </td>
                {editable ? (
                  <td className={CELDA}>
                    {/* Opcion A, libertad total: cualquier linea se puede quitar,
                        tambien las del bono. Es recuperable: queda tachada y con
                        "Restaurar". Una linea anadida a mano, al quitarla, se
                        elimina de verdad (no tiene sentido restaurar un extra). */}
                    {borrable ? (
                      quitada ? (
                        <button
                          type="button"
                          onClick={() => onAlternarEliminada?.(indice)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-acento transition-colors hover:bg-elevado"
                        >
                          Restaurar
                        </button>
                      ) : indice >= desde ? (
                        <button
                          type="button"
                          aria-label={`Quitar ${linea.nombre}`}
                          onClick={() =>
                            onCambiarAnadidas?.(anadidas.filter((_, i) => i !== indice - desde))
                          }
                          className="rounded-lg p-1 text-tinta-apagada transition-colors hover:bg-elevado hover:text-tinta"
                        >
                          <IconoQuitar aria-hidden="true" className="size-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Quitar ${linea.nombre}`}
                          onClick={() => onAlternarEliminada?.(indice)}
                          className="rounded-lg p-1 text-tinta-apagada transition-colors hover:bg-elevado hover:text-tinta"
                        >
                          <IconoQuitar aria-hidden="true" className="size-4" />
                        </button>
                      )
                    ) : indice >= desde ? (
                      <button
                        type="button"
                        aria-label={`Quitar ${linea.nombre}`}
                        onClick={() =>
                          onCambiarAnadidas?.(anadidas.filter((_, i) => i !== indice - desde))
                        }
                        className="rounded-lg p-1 text-tinta-apagada transition-colors hover:bg-elevado hover:text-tinta"
                      >
                        <IconoQuitar aria-hidden="true" className="size-4" />
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-borde bg-elevado">
              <td className={`${CELDA} font-semibold text-tinta`} colSpan={4}>
                Base imponible del pedido
              </td>
              <td className={`${CELDA} font-semibold text-tinta`}>{formatearEuros(total)}</td>
              {editable ? <td className={CELDA} /> : null}
            </tr>
          </tfoot>
        </table>
      </div>

      {historial?.encontrado ? (
        <p className="text-sm text-tinta-apagada">
          Ultimo pedido de este cliente: {historial.presupuesto || 'sin numero'}
          {historial.fecha ? ` del ${formatearFecha(historial.fecha)}` : ''}
          {anterior === null ? '' : `, ${formatearEuros(anterior)} de base`}
          {historial.confirmado ? '' : ' (seguia en borrador, era una propuesta)'}.
        </p>
      ) : (
        <p className="text-sm text-tinta-apagada">
          {cargandoHistorial
            ? 'Buscando el ultimo pedido de este cliente.'
            : 'Este cliente no tiene ninguna factura anterior en Holded con la que comparar.'}
        </p>
      )}

      {diferencias ? (
        <p className="rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta">
          Hay precios que no coinciden con lo que este cliente pago la ultima vez. El precio sale
          del catalogo de Holded, que esta por debajo de lo que se viene cobrando. Corrigelo aqui si
          no es lo que quieres publicar.
        </p>
      ) : null}
    </div>
  )
}
